import { InverterAction } from '../types/global';
import { computeEnergy, estimateCost, describeFlow, formatIntervalValue } from './utils';

// Maps live action + reason to customer-friendly what/why copy.
export type ActionInterpretation = {
  actionLabel: string;
  simpleExplanation: string;
  customerSafeReason: string;
};

// Action strings on InverterAction follow `{battery}-{solar}` — e.g.
// `discharge-export`, `charge-curtail`, `auto-curtail`.
export function parseAction(action: string): { battery: string; solar: string } {
  const parts = action?.split('-') ?? [];
  return { battery: parts[0] ?? 'unknown', solar: parts[1] ?? '' };
}

// One-word label for compact UI (timeline cells, status rows). Pair with
// interpretAction() when you need a full sentence.
export function shortLabel(battery: string): string {
  switch (battery) {
    case 'charge': return 'Charging';
    case 'discharge': return 'Discharging';
    case 'auto': return 'Auto';
    case 'stopped': return 'Idle';
    case 'fullstop': return 'Stopped';
    case 'export': return 'Exporting';
    case 'import': return 'Importing';
    default: return battery || 'Unknown';
  }
}

// Single entry point for turning an action + reason into customer copy.
//
// `userReason` is the backend-generated, customer-facing reason
// (decision_logger.get_user_reason(), surfaced on the action values). When set
// it wins for `customerSafeReason` — this covers both the auto-tuned decision
// engine and hacker-mode users who set their own reason. When absent (e.g. a
// hacker-mode script that never touched `decisions`), we fall back to deriving
// the copy locally from the action + raw reason string. The `actionLabel` /
// `simpleExplanation` are always derived locally so the headline stays in sync
// with the cells regardless of the override.
export function interpretAction(
  action: string,
  reason: string,
  sellPrice?: number,
  feedInLimit?: number | null,
  userReason?: string | null,
): ActionInterpretation {
  const base = deriveInterpretation(action, reason, sellPrice, feedInLimit);
  if (typeof userReason === 'string' && userReason.trim())
    return { ...base, customerSafeReason: userReason.trim() };
  return base;
}

function deriveInterpretation(
  action: string,
  reason: string,
  sellPrice?: number,
  feedInLimit?: number | null,
): ActionInterpretation {
  const a = (action || '').toLowerCase();
  const r = (reason || '').toLowerCase();
  const has = (...keys: string[]) => keys.some(k => r.includes(k));

  // export100 — or feed_in_power_limitation < 1000W — defensive export to avoid importing at high prices
  if (a.includes('export100') || (a.includes('export') && feedInLimit != null && feedInLimit < 1000))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Pushing a little extra to the grid. Avoiding high buy prices.', customerSafeReason: 'Grid prices are high right now. Powston is exporting a little extra to make sure your home doesn\'t risk buying any expensive energy.' };

  // export with a capped feed-in limit (≥ 1000W) — spreading available export evenly
  if (a.includes('export') && feedInLimit != null && feedInLimit >= 1000)
    return { actionLabel: 'Exporting to the grid', simpleExplanation: `Exporting to the grid, capped at ${Math.round(feedInLimit / 1000)}kW.`, customerSafeReason: `Your battery is exporting energy back to the grid, spreading it evenly at ${Math.round(feedInLimit / 1000)}kW to get the best return from what's available.` };

  // export at modest sell price (< 30¢) — extra capacity, selling at best available to reduce daily costs
  if (a.includes('export') && sellPrice !== undefined && sellPrice < 0.30)
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Selling at the best price available.', customerSafeReason: 'Your battery has extra capacity. Powston is selling at the best price available to lower your daily costs.' };

  if (a.includes('export') && has('max price', 'high now', 'peak sell', 'profitable margin', 'good price'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Exporting to the grid. Sell price is high.', customerSafeReason: 'Your battery is selling energy back to the grid. You\'re earning money right now.' };

  if (a.includes('import_no_solar') && has('negative prices', 'negative wholesale', 'negative price'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Energy is better than free right now.', customerSafeReason: 'Buying energy is getting you paid. Powston buying as much as possible.' };

  if (a.includes('import') && has('negative prices', 'negative wholesale', 'negative price'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Energy is almost free right now.', customerSafeReason: 'Energy is almost free right now. Powston is storing it to sell later at a profit.' };

  if (a.includes('charge') && has('negative wholesale', 'charging from solar'))
    return { actionLabel: 'Charging from solar', simpleExplanation: 'Charging from solar. Storing free energy for later.', customerSafeReason: 'Your panels are generating free energy. Powston is storing it for when it\'s worth more.' };

  if (has('curtailing all exports', 'export price is negative'))
    return { actionLabel: 'Standing by', simpleExplanation: 'Standing by. Waiting for the right moment to sell.', customerSafeReason: 'Prices are low right now. Powston is waiting for the right moment to sell.' };

  if (a.includes('export') && has('dump before the price goes negative', 'morning dumping'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Exporting to the grid. Making room for cheaper energy later.', customerSafeReason: 'Powston is making room in your battery for cheaper energy later today.' };

  if (a.includes('import') && has('low soc', 'before evening peak', 'protect battery', 'topping up'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Topping up before prices rise.', customerSafeReason: 'Powston is topping up your battery before energy prices rise tonight.' };

  if (a.includes('auto') && has('waiting for more solar', 'strong solar forecast', 'delaying import'))
    return { actionLabel: 'Waiting for solar', simpleExplanation: 'Solar is on the way. Saving space for free energy.', customerSafeReason: 'Solar is forecast for later. Powston is saving space in your battery for free energy.' };

  if (a.includes('auto') && has('better sell opportunities', 'conserving remaining battery', 'conserving battery'))
    return { actionLabel: 'Holding', simpleExplanation: 'Holding energy for a better price later.', customerSafeReason: 'Powston is holding your stored energy for a better price later today.' };

  // Fallbacks by action
  if (a.includes('export'))
    return { actionLabel: 'Exporting to the grid', simpleExplanation: 'Exporting to the grid. Earning money right now.', customerSafeReason: 'Your battery is selling energy back to the grid. You\'re earning money right now.' };
  if (a.includes('import'))
    return { actionLabel: 'Importing from the grid', simpleExplanation: 'Importing from the grid. Topping up while energy is affordable.', customerSafeReason: 'Powston is topping up your battery while energy is affordable.' };
  // Discharge must come before charge — "discharge" contains the substring
  // "charge", so a.includes('charge') would otherwise swallow every discharge.
  if (a.includes('discharge'))
    return { actionLabel: 'Discharging', simpleExplanation: 'Your battery is powering your home.', customerSafeReason: 'Your battery is powering your home and reducing your bill.' };
  if (a.includes('charge'))
    return { actionLabel: 'Charging from solar', simpleExplanation: 'Charging from solar. Storing free energy for later.', customerSafeReason: 'Your battery is charging from solar. Free energy, stored for later.' };
  if (a.includes('auto'))
    return { actionLabel: 'Optimising', simpleExplanation: 'Watching the market, ready to act.', customerSafeReason: 'Powston is watching the market, ready to act.' };

  return { actionLabel: 'Monitoring', simpleExplanation: 'Watching the market, ready to act.', customerSafeReason: 'Powston is watching the market, ready to act.' };
}

// Easy, customer-safe one-liner for an interval's reason — the plain-language
// "why" to show on summary/display surfaces, with the raw reason + full
// decision logic reserved for drill-down. Centralises the values parsing so
// callers don't each re-derive sell price / feed-in limit.
export function customerReason(action: InverterAction): string {
  const parsed = (() => { try { return JSON.parse(action.values); } catch { return {}; } })();
  const sellPriceCents = action.sell_price ?? parsed?.sell_price ?? 0;
  const feedInLimit = parsed?.feed_in_power_limitation ?? null;
  // Prefer the backend-generated reason; fall back to local interpretation.
  const userReason = parsed?.user_reason ?? parsed?.decisions?.user_reason ?? null;
  return interpretAction(action.action, action.reason, sellPriceCents / 100, feedInLimit, userReason).customerSafeReason;
}

// Plain-language interpretation of a single interval — answers "does this make
// sense?" without making the user do the maths. Pure (no JSX) so the desktop
// table and mobile card render the identical text. Reuses interpretAction for
// the headline and the shared describeFlow/formatIntervalValue helpers so the
// wording can't contradict the cells.
export type IntervalExplanation = {
  headline: string;
  energyLine: string;
  valueLine: string;
  verificationLine: string;
  rounded: boolean;
  isSettled: boolean;
};

export function buildIntervalExplanation(action: InverterAction): IntervalExplanation {
  const parsed = (() => { try { return JSON.parse(action.values); } catch { return {}; } })();
  const sellPriceCents = action.sell_price ?? parsed?.sell_price ?? 0;
  const buyPriceCents = action.buy_price ?? parsed?.buy_price ?? 0;
  const feedInLimit = parsed?.feed_in_power_limitation ?? null;

  const interp = interpretAction(action.action, action.reason, sellPriceCents / 100, feedInLimit);
  const headline = `${interp.actionLabel}. ${interp.simpleExplanation}`;

  const flow = describeFlow(action);
  const value = formatIntervalValue(action);
  const v = estimateCost(action); // dollars; negative = profit
  const energyKWh = computeEnergy(action);

  let energyLine: string;
  let valueLine = '';
  if (flow.word === 'Idle') {
    energyLine = 'No grid flow this interval — energy stayed on site.';
  } else {
    const verb = flow.word === 'Export' ? 'Exported' : 'Imported';
    energyLine = `${verb} ${flow.kwh} this interval (about ${flow.kw} for these 5 minutes).`;
    const priceCents = flow.word === 'Export' ? sellPriceCents : buyPriceCents;
    const valuePhrase = value.word ? `${value.word.toLowerCase()} ${value.amountText}` : value.amountText;
    valueLine = `At ${priceCents.toFixed(0)}¢/kWh that worked out to ${valuePhrase} for this interval.`;
  }

  // Genuine no-flow uses the "no grid flow" line above, not the rounding note.
  const rounded = Math.abs(v) < 0.005 && Math.abs(energyKWh) >= 0.0005;
  if (rounded) {
    valueLine += ` The value is under half a cent for this interval, so it rounds to ${value.amountText}. That's expected for a single 5-minute window.`;
  }

  const isSettled = action.quality === 'Act';
  const verificationLine = isSettled
    ? 'Settled against your meter data. Retailers can still revise "actual" figures until after midday the following day, so treat this as near-final until then.'
    : 'Estimated from inverter readings. The figure firms up once your retailer sends meter data — from around midday the day after, and it can keep settling for a little while after that.';

  return { headline, energyLine, valueLine, verificationLine, rounded, isSettled };
}
